import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Save, Landmark, FilePlus2 } from 'lucide-react';
import type { Customer } from '@ach/shared';
import { customerInputSchema, formatUsd, REQUEST_STATUS_LABELS } from '@ach/shared';

import { api, ApiError } from '../lib/api.js';
import { Field, Spinner, ErrorNote, InfoNote } from './components.js';

interface SavedAccount {
  id: string;
  bankName: string;
  accountType: 'checking' | 'savings';
  accountLast4: string;
  accountHolderName: string | null;
}

interface RequestRow {
  id: string;
  status: keyof typeof REQUEST_STATUS_LABELS;
  total_amount_cents: number;
  created_at: string;
}

const BLANK = {
  name: '',
  company: '',
  email: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  notes: '',
};

export function CustomerEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [form, setForm] = useState({ ...BLANK });
  const [savedAccounts, setSavedAccounts] = useState<SavedAccount[]>([]);
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isNew) return;
    api
      .get<{ customer: Customer; savedAccounts: SavedAccount[]; requests: RequestRow[] }>(
        `/api/admin/customers/${id}`,
      )
      .then((data) => {
        setForm({
          name: data.customer.name,
          company: data.customer.company ?? '',
          email: data.customer.email,
          phone: data.customer.phone ?? '',
          addressLine1: data.customer.addressLine1 ?? '',
          addressLine2: data.customer.addressLine2 ?? '',
          city: data.customer.city ?? '',
          state: data.customer.state ?? '',
          postalCode: data.customer.postalCode ?? '',
          country: data.customer.country,
          notes: data.customer.notes ?? '',
        });
        setSavedAccounts(data.savedAccounts);
        setRequests(data.requests);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Could not load that customer.'))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const set = (key: keyof typeof BLANK) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((previous) => ({ ...previous, [key]: event.target.value }));
    setFieldErrors((previous) => ({ ...previous, [key]: '' }));
  };

  async function save(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setFieldErrors({});

    // Validate with the same schema the server uses, so the two cannot drift
    // and produce a field that passes here and is rejected there.
    const parsed = customerInputSchema.safeParse(form);
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path.join('.');
        errors[path] ??= issue.message;
      }
      setFieldErrors(errors);
      setSaving(false);
      return;
    }

    try {
      const result = isNew
        ? await api.post<{ customer: Customer }>('/api/admin/customers', parsed.data)
        : await api.patch<{ customer: Customer }>(`/api/admin/customers/${id}`, parsed.data);
      navigate(`/admin/customers/${result.customer.id}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        if (e.fields) setFieldErrors(e.fields);
      } else {
        setError('Could not save.');
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Spinner label="Loading customer" />;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/admin/customers" className="text-slate-500 hover:text-slate-800">
          <ArrowLeft className="h-5 w-5" aria-label="Back to customers" />
        </Link>
        <h1 className="text-xl font-semibold text-slate-900">
          {isNew ? 'New customer' : form.name || 'Customer'}
        </h1>
        {!isNew && (
          <Link to={`/admin/requests/new?customer=${id}`} className="btn-primary ml-auto">
            <FilePlus2 className="h-4 w-4" aria-hidden />
            New payment request
          </Link>
        )}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      <form onSubmit={save} className="card space-y-5 p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" required error={fieldErrors.name}>
            <input
              className={`field-input ${fieldErrors.name ? 'field-input-error' : ''}`}
              value={form.name}
              onChange={set('name')}
              autoFocus={isNew}
            />
          </Field>
          <Field label="Company" error={fieldErrors.company}>
            <input className="field-input" value={form.company} onChange={set('company')} />
          </Field>
          <Field
            label="Email"
            required
            error={fieldErrors.email}
            hint="Where the payment request is sent."
          >
            <input
              type="email"
              className={`field-input ${fieldErrors.email ? 'field-input-error' : ''}`}
              value={form.email}
              onChange={set('email')}
            />
          </Field>
          <Field label="Phone" error={fieldErrors.phone}>
            <input className="field-input" value={form.phone} onChange={set('phone')} />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Address" error={fieldErrors.addressLine1}>
            <input className="field-input" value={form.addressLine1} onChange={set('addressLine1')} />
          </Field>
          <Field label="Address line 2" error={fieldErrors.addressLine2}>
            <input className="field-input" value={form.addressLine2} onChange={set('addressLine2')} />
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-4">
          <Field label="City" error={fieldErrors.city}>
            <input className="field-input" value={form.city} onChange={set('city')} />
          </Field>
          <Field label="State" error={fieldErrors.state}>
            <input className="field-input" value={form.state} onChange={set('state')} />
          </Field>
          <Field
            label="ZIP"
            error={fieldErrors.postalCode}
            hint="Can be used as the payment link's security question."
          >
            <input className="field-input" value={form.postalCode} onChange={set('postalCode')} />
          </Field>
          <Field label="Country" error={fieldErrors.country}>
            <input className="field-input" value={form.country} onChange={set('country')} maxLength={2} />
          </Field>
        </div>

        <Field label="Notes" error={fieldErrors.notes}>
          <textarea className="field-input" rows={3} value={form.notes} onChange={set('notes')} />
        </Field>

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Link to="/admin/customers" className="btn-secondary">
            Cancel
          </Link>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Save className="h-4 w-4" aria-hidden />
            )}
            {isNew ? 'Create customer' : 'Save changes'}
          </button>
        </div>
      </form>

      {!isNew && (
        <>
          <section className="space-y-3">
            <h2 className="flex items-center gap-2 font-semibold text-slate-900">
              <Landmark className="h-4 w-4 text-slate-400" aria-hidden />
              Bank accounts on file
            </h2>
            {savedAccounts.length === 0 ? (
              <InfoNote>
                No bank accounts yet. One is saved the first time this customer submits a payment.
              </InfoNote>
            ) : (
              <div className="card divide-y divide-slate-100">
                {savedAccounts.map((account) => (
                  <div key={account.id} className="flex items-center gap-3 px-4 py-3 text-sm">
                    <span className="font-medium text-slate-800">{account.bankName}</span>
                    <span className="text-slate-500">
                      {account.accountType === 'savings' ? 'Savings' : 'Checking'} ••••
                      {account.accountLast4}
                    </span>
                    {account.accountHolderName && (
                      <span className="text-slate-400">{account.accountHolderName}</span>
                    )}
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-slate-500">
              Full account numbers are encrypted and only visible from a submission, with two-factor
              authentication. Every time one is shown it is written to the access log.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-semibold text-slate-900">Payment requests</h2>
            {requests.length === 0 ? (
              <InfoNote>No payment requests for this customer yet.</InfoNote>
            ) : (
              <div className="card divide-y divide-slate-100">
                {requests.map((request) => (
                  <Link
                    key={request.id}
                    to={`/admin/requests/${request.id}`}
                    className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-slate-50"
                  >
                    <span className="text-slate-500">
                      {new Date(request.created_at).toLocaleDateString()}
                    </span>
                    <span className="font-medium text-slate-800">
                      {formatUsd(request.total_amount_cents)}
                    </span>
                    <span className="ml-auto text-slate-500">
                      {REQUEST_STATUS_LABELS[request.status]}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
